import { attr, belongsTo } from '@ember-data/model';
import { service } from '@ember/service';
import { Type } from '@warp-drive/core-types/symbols';
import moment from 'moment-timezone';
import BaseModel from 'volta/models/base-model';
import { resourceCommand } from 'volta/utils/api';
import { ForecastFrequencyOptions } from './forecast-project-header';

import type { IResourceName } from 'volta/models/base-model';
import type { IAggTasksViewMap } from 'volta/models/types/tasks';
import type ForecastProjectHeader from 'volta/models/forecast-project-header';
import type UserProfile from 'volta/models/user-profile';
import type SessionUserService from 'volta/services/session-user';
import type { THueVariationString } from 'volta/utils/style-utils';
import type { TExternalForecastField, TForecastGroupField } from './external-forecast';
import type {
  IRefreshForecastProjectSelectionResult,
  IUpdateForecastProjectCmd,
  TForecastProjectStats,
  TForecastProjectStatus
} from './types/forecast-projects';
import type Simulation from './simulation';

export const ForecastProjectResourceName: IResourceName = {
  singular: 'forecastProject',
  plural: 'forecastProjects'
};

export default class ForecastProject extends BaseModel {
  @service declare sessionUser: SessionUserService;

  // Commands

  UpdateForecastProject = resourceCommand<IUpdateForecastProjectCmd, ForecastProject>(
    'UpdateForecastProject',
    { query: { include: 'header,simulation' } }
  );

  ValidateForecastProject = resourceCommand<{fpHeaderId: string}, ForecastProject>('ValidateForecastProject', {
    query: { include: 'header,simulation' }
  });

  RefreshForecastProjectSelection = resourceCommand<
    IRefreshForecastProjectSelectionResult,
    ForecastProject
  >('RefreshForecastProjectSelection', { query: { include: 'header,simulation' } });

  DeleteForecastProject = resourceCommand<{ fpHeaderId: string }, ForecastProject>('DeleteForecastProject');

  CreateSimulationFromRevision = resourceCommand<{ forecastTypes: string[], fpHeaderId: string }, ForecastProject>(
    'CreateSimulationFromRevision',
    { query: { include: 'header,simulation' } }
  );

  // Attributes

  @attr('string')
  declare fullName: string;

  @attr('string')
  declare fpHeaderId: string;

  @attr('string')
  declare simulationId: string;

  @attr('string')
  declare revision?: string;

  @attr('number', { defaultValue: 0 })
  declare revisionNumber: number;

  @attr('array')
  defaultGroupBy?: TExternalForecastField[][];

  @attr('string')
  declare status: TForecastProjectStatus;

  @attr('number', { defaultValue: 0 })
  declare validationCompletion: number;

  @belongsTo('forecast-project-header', { async: false, inverse: null })
  declare header?: ForecastProjectHeader;

  @attr('string')
  declare createdBy: string;

  @attr('string')
  updatedBy?: string;

  @attr('string')
  validatedBy?: string;

  @attr('date')
  validatedAt?: Date;

  @attr('date')
  deactivatedAt?: Date;

  /**
   * Aggregated data about each task type
   */
  @attr({ defaultValue: () => ({}) })
  declare tasks: IAggTasksViewMap;

  @attr({ defaultValue: () => ({ skuCount: 0, sourceCount: 0, excludedSkuCount: 0 }) })
  declare stats: TForecastProjectStats;

  @belongsTo('simulation', { async: false, inverse: null })
  simulation?: Simulation;

  get effectiveDefaultGroupBy() {
    return this.defaultGroupBy ?? this.header?.defaultGroupBy;
  }

  get name() {
    return this.header?.name ?? '';
  }

  get description() {
    return this.header?.description;
  }

  get skuSelection() {
    return this.header?.skuSelection ?? {};
  }

  get forecastFrequency() {
    return this.header?.forecastFrequency ?? 'W';
  }

  get forecastHorizon() {
    return this.header?.forecastHorizon ?? 0;
  }

  get forecastQuantiles() {
    return this.header?.forecastQuantiles;
  }

  get forecastTypes() {
    return this.header?.forecastTypes;
  }

  get selectedManualSources() {
    return this.header?.selectedManualSources;
  }

  get contributors() {
    return this.header?.contributors ?? [];
  }

  get permissions() {
    return this.header?.permissions ?? {};
  }

  get contributorIds() {
    return this.header?.contributorIds ?? [];
  }

  get metadataModel() {
    return this.header?.mlPipeline?.metadataModel ?? [];
  }

  get rtsModel() {
    return this.header?.mlPipeline?.rtsModel ?? [];
  }

  get mlPipelineModelName() {
    return this.header?.mlPipeline?.modelName;
  }

  get scheduleId() {
    return this.header?.mlPipeline?.scheduleId;
  }

  // Getters

  get isTemplate() {
    return this.revisionNumber === 0;
  }

  get isReadyForReview() {
    return this.status === 'READY_FOR_REVIEW';
  }

  get isValidated() {
    return this.status === 'VALIDATED';
  }

  get isOngoing() {
    return ['READY_FOR_REVIEW', 'INITIALISED', 'EDITED'].includes(this.status);
  }

  get horizonAsDuration() {
    return moment
      .duration(this.header?.forecastHorizon ?? 0, this.forecastFrequencyOption.period)
      .toString();
  }

  get contributorsRegistry() {
    return (this.header?.contributors ?? []).reduce(
      (acc, c) => {
        if (c.id) {
          acc[c.id] = c;
        }
        return acc;
      },
      {} as Record<string, UserProfile>
    );
  }

  get statusOption() {
    return ForecastProjectStatusColors[this.status];
  }

  get forecastFrequencyOption() {
    return ForecastFrequencyOptions[this.forecastFrequency];
  }

  get filtersCount() {
    return Object.keys(this.header?.skuSelection ?? {}).length;
  }

  get statusUpdateLabel() {
    const { contributorsRegistry, status } = this;
    if (status === 'DEACTIVATED') {
      return {
        label: 'deactivatedWhenBy',
        date: this.deactivatedAt,
        user: this.header?.createdBy ? contributorsRegistry[this.header.createdBy]?.login : 'VOLTA'
      };
    } else if (status === 'EDITED' || status === 'READY_FOR_REVIEW') {
      return {
        label: 'updatedWhenBy',
        date: this.updatedAt,
        user: this.updatedBy ? contributorsRegistry[this.updatedBy]?.login : 'VOLTA'
      };
    } else if (status === 'VALIDATED') {
      return {
        label: 'validatedWhenBy',
        date: this.validatedAt,
        user: this.validatedBy ? contributorsRegistry[this.validatedBy]?.login : 'VOLTA'
      };
    }

    return {
      label: 'createdWhenBy',
      date: this.createdAt,
      user: this.header?.createdBy
        ? contributorsRegistry[this.header.createdBy]?.login
        : contributorsRegistry[this.createdBy]?.login
    };
  }

  get validationCompletionPct() {
    return 100 * this.validationCompletion;
  }

  updateCompletion(completion: number) {
    if (completion !== this.validationCompletion) {
      this.validationCompletion = completion;

      if (completion === 1) {
        this.status = 'VALIDATED';
      }

      if (completion < 1 && this.status === 'READY_FOR_REVIEW') {
        this.status = 'EDITED';
      }
    }
  }

  canUserDo = (action: string) => {
    return this.header?.canUserDo(action) ?? false;
  };

  [Type] = 'forecast-project' as const;
}

export const ForecastProjectStatusColors: Record<
  TForecastProjectStatus,
  { color?: THueVariationString; icon?: string; label: string }
> = {
  INITIALISED: { color: 'gray', icon: 'sparkles', label: 'forecastProjectStatuses.INITIALISED' },
  EDITED: { color: 'purple', icon: 'edit', label: 'forecastProjectStatuses.EDITED' },
  DEACTIVATED: { color: 'red', icon: 'close', label: 'forecastProjectStatuses.DEACTIVATED' },
  VALIDATED: { color: 'green', icon: 'checkmark', label: 'forecastProjectStatuses.VALIDATED' },
  READY_FOR_REVIEW: {
    color: 'orange',
    icon: 'eye',
    label: 'forecastProjectStatuses.READY_FOR_REVIEW'
  }
} as const;

export const GroupOptions: Record<
  TExternalForecastField,
  {
    color?: THueVariationString;
    label?: string;
    valueLabel?: (f: TForecastGroupField) => string;
    excludeFromSelection?: boolean;
  }
> = {
  sku: { color: 'blue' },
  part: { color: 'blue', label: 'partCode' },
  warehouse: { color: 'green' },
  supplier: { color: 'primary' },
  workshop: { color: 'orange' },
  deliveryWorkshop: { color: 'purple' },
  acqCode: { valueLabel: (f) => `acqCodes.${f.value}` },
  methodology: { valueLabel: (f) => `planningMethodologies.${f.value}` },
  partType: { color: 'indigo' },
  partUnitMeasure: { valueLabel: (f) => `unitMeasures.${f.value}` },
  reorderStrategy: { valueLabel: (f) => `reorderStrategies.${f.value}` },
  buyer: {},
  orderType: {},
  planner: {},
  source: {},
  'sku.abcClass': { label: 'abcClass' },
  'sku.xyzClass': { label: 'xyzClass' },
  'sku.leadTimeCategory': {
    label: 'leadTimeCategory',
    valueLabel: (f) => `leadTimeCategories.${f.value}`
  },
  'sku.varCategory': { label: 'varCategory', valueLabel: (f) => `varCategories.${f.value}` },
  'period.day': { excludeFromSelection: true },
  'period.week': { excludeFromSelection: true },
  'period.month': { excludeFromSelection: true },
  'period.quarter': { excludeFromSelection: true },
  'period.year': { excludeFromSelection: true }
};
